import { forwardRef, type ButtonHTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import MuiIconButton from '@mui/material/IconButton';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';

export type IconButtonColor = 'primary' | 'secondary' | 'warning' | 'info' | 'error' | 'success';
export type IconButtonVariant = NonNullable<MuiButtonProps['variant']>;
export type IconButtonShape = 'square' | 'circle';
/** `compact`: 10px icon inside the button (e.g. a chevron), whatever the button size. */
export type IconButtonIconSize = 'default' | 'compact';

export type IconButtonProps = {
  children?: ReactNode;
  color?: IconButtonColor;
  variant?: IconButtonVariant;
  /** @default 'square' */
  shape?: IconButtonShape;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** @default 'default' */
  iconSize?: IconButtonIconSize;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'color' | 'children' | 'onClick' | 'disabled' | 'size' | 'className' | 'style' | 'sx'
>;

/** Thin wrapper around MUI IconButton with design-system color and variant. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    color = 'primary',
    variant = 'contained',
    shape = 'square',
    children,
    disabled,
    size = 'medium',
    iconSize = 'default',
    onClick,
    'aria-label': ariaLabel,
    ...rest
  },
  ref
) {
  return (
    <MuiIconButton
      ref={ref}
      {...rest}
      disabled={disabled}
      size={size}
      onClick={onClick}
      aria-label={ariaLabel}
      disableRipple={variant === 'text'}
      className={[
        'SmIconButton-root',
        `SmIconButton-color-${color}`,
        `SmIconButton-variant-${variant}`,
        `SmIconButton-shape-${shape}`,
        iconSize === 'compact' && 'SmIconButton-icon-compact'
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </MuiIconButton>
  );
});
